import type { CreateWorkAreaMutationVariables, UpdateWorkAreaMutationVariables } from "@/types/gql/graphql";
import { CurbZoneMapProps, CurbZonePageModeType } from "../curb-zone.type";
import {
  FeatureCollection as DeckFeatureCollection,
  EditableGeoJsonLayer,
  EditAction,
} from "@deck.gl-community/editable-layers";
import { FeatureCollection, Polygon } from "geojson";
import { WorkArea, WorkAreaProperties } from "@/modules/curb-zone/components/work-area/work-area.type";
import { curbZoneGeoJsonLayerModes, workAreaMode } from "@/modules/curb-zone/curb-zone-map.const";
import { useContext, useEffect, useMemo, useState } from "react";

import { CityContext } from "@/contexts/city-context-provider";
import { PickingInfo } from "deck.gl";
import { filterFeatureIndex } from "@/helper/map-utils";
import { useQueryWorkArea } from "./use-query-work-area";
import useToggle from "@/hooks/use-toggle";

type UseWorkAreaParams = Pick<CurbZoneMapProps, "appMode">;

export const useWorkArea = ({ appMode }: UseWorkAreaParams) => {
  const { currentCity } = useContext(CityContext);
  const [openWorkAreaModal, setOpenWorkAreaModal] = useToggle(false);
  const [workAreaEditionData, setWorkAreaEditionData] = useState<FeatureCollection<Polygon>>({
    type: "FeatureCollection",
    features: [],
  });
  const [workAreaDetail, setWorkAreaDetail] = useState<WorkArea>();
  const { workAreas, loading: workAreaLoading, handleUpdateWorkArea, handleAddWorkArea } = useQueryWorkArea();
  const workAreaLayerIndexes = useMemo(
    () => filterFeatureIndex(workAreaEditionData, workAreaDetail),
    [workAreaEditionData, workAreaDetail],
  );

  const showWorkAreaDetail = useMemo(
    () => appMode === CurbZonePageModeType.EditWorkArea && workAreaDetail,
    [appMode, workAreaDetail],
  );

  const resetDataOfEditState = () => {
    setWorkAreaDetail(undefined);
  };

  /**
   * If the user clicks outside a work-area, deselect a work-area
   */
  const handleSelectWorkArea = (info: PickingInfo) => {
    if (info.layer?.id !== "work-areas-layer" && workAreaDetail) {
      resetDataOfEditState();
    }
  };

  const onEditWorkAreaLayer = async ({ updatedData, editType }: EditAction<FeatureCollection<Polygon, WorkArea>>) => {
    setWorkAreaEditionData(updatedData);
    if (editType === "finishMovePosition" || editType === "addFeature") {
      if (appMode === CurbZonePageModeType.AddWorkArea) {
        setOpenWorkAreaModal();
      } else {
        await saveWorkAreaProperties();
      }
    }
  };

  const onClickWorkAreaLayer = (info: PickingInfo) => {
    if (appMode !== CurbZonePageModeType.EditWorkArea) {
      return;
    }

    if (info.object) {
      if (info.object && (!info.object.id || info.object.id === workAreaDetail?.id)) {
        //Click an existed work-area or remove a point of polygon
        return;
      }
      //Select a work-area
      setWorkAreaDetail(info.object);
    }
  };

  const saveWorkAreaProperties = async (promptedProperties?: WorkAreaProperties) => {
    if (appMode === CurbZonePageModeType.AddWorkArea) {
      const lastWorkAreaFeature = workAreaEditionData.features.at(-1);
      if (!lastWorkAreaFeature) return;
      const createWorkAreaParam: CreateWorkAreaMutationVariables = {
        workAreaInput: {
          name: promptedProperties?.name ?? "",
          cityId: currentCity?.id ?? "",
          geometry: lastWorkAreaFeature.geometry,
        },
      };
      await handleAddWorkArea(createWorkAreaParam);
    } else {
      if (!workAreaDetail) return;
      const updateWorkAreaParam: UpdateWorkAreaMutationVariables = {
        workAreaInput: {
          ...(promptedProperties || workAreaEditionData.features[workAreaLayerIndexes[0]]),
          id: workAreaDetail?.id,
          cityId: currentCity?.id ?? "",
          geometry: workAreaEditionData.features[workAreaLayerIndexes[0]].geometry,
        },
      };
      const response = await handleUpdateWorkArea(updateWorkAreaParam);
      if (response?.updateWorkArea) {
        setWorkAreaDetail(response.updateWorkArea);
      }
      if (promptedProperties) {
        resetDataOfEditState();
      }
    }
    if (openWorkAreaModal) {
      setOpenWorkAreaModal();
    }
  };

  const editableGeoJsonLayerMode = useMemo(() => {
    if (workAreaMode.includes(appMode)) {
      return curbZoneGeoJsonLayerModes[appMode];
    }
    return curbZoneGeoJsonLayerModes[CurbZonePageModeType.View];
  }, [appMode]);

  const editableWorkAreaLayer = new EditableGeoJsonLayer({
    id: "work-areas-layer",
    data: workAreaEditionData as DeckFeatureCollection,
    mode: editableGeoJsonLayerMode,
    selectedFeatureIndexes: workAreaLayerIndexes,
    lineWidthMinPixels: 1,
    lineWidthMaxPixels: 1,
    getFillColor: [0, 0, 0, 0],
    autoHighlight: workAreaMode.includes(appMode),
    getLineColor: [54, 0, 171, 255],
    onEdit: (updatedData) => onEditWorkAreaLayer(updatedData as EditAction<FeatureCollection<Polygon, WorkArea>>),
    onClick: onClickWorkAreaLayer,
    updateTriggers: {
      mode: [appMode],
    },
  });

  const workAreaLayers = [editableWorkAreaLayer];

  useEffect(() => {
    if (!currentCity || workAreaLoading) {
      return;
    }
    setWorkAreaEditionData({
      type: "FeatureCollection",
      features: workAreas,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workAreaLoading, workAreas]);

  return {
    openWorkAreaModal,
    setOpenWorkAreaModal,
    workAreaEditionData,
    setWorkAreaEditionData,
    workAreaDetail,
    setWorkAreaDetail,
    showWorkAreaDetail,
    workAreaLayerIndexes,
    handleSelectWorkArea,
    workAreaLayers,
    saveWorkAreaProperties,
    resetDataOfEditState,
  };
};
